import { computed } from 'vue';

import { DataSourceSettingsTabType } from '@/components/data-sources/SettingsModal/constants';

import { getFeedStatusUi, statuses } from '@/composables/useFeedDetails';
import { HS3_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { useFeedsStore } from '@/stores';
import { IntegrationProfileStatus } from '@/types/integration_profiles';

const HEALTHY_FEED_STATUSES: string[] = [statuses.ACTIVE, statuses.SETTING_UP];

export default function useHubspotCopilotStatus() {
  const feedsStore = useFeedsStore();

  const hubspotFeed = computed(() =>
    feedsStore.getFeedByDataSourceType(HS3_DATA_SOURCE_TYPE),
  );

  const feedStatus = computed(() => getFeedStatusUi(hubspotFeed.value));

  const feedStatusName = computed(() => feedStatus.value.type ?? '');

  const feedState = computed(() => {
    if (!feedsStore.ready) return 'checking';
    if (feedsStore.error) return 'unknown';
    if (!hubspotFeed.value) return 'missing';
    return feedStatus.value.severity ? 'known' : 'unknown';
  });

  const statusType = computed<IntegrationProfileStatus>(() => {
    if (feedState.value === 'missing') return 'error';
    if (feedState.value !== 'known') return 'warning';
    return feedStatus.value.severity ?? 'warning';
  });

  const statusText = computed(() => {
    if (feedState.value === 'checking') return 'Checking…';
    if (feedState.value === 'unknown') return 'Status unknown';
    if (feedState.value === 'missing') return 'Not connected';
    return feedStatus.value.text ?? 'Status unknown';
  });

  const needsFeedAttention = computed(() => {
    if (feedState.value === 'missing') return true;
    if (feedState.value !== 'known') return false;
    return !HEALTHY_FEED_STATUSES.includes(feedStatusName.value);
  });

  const feedSettingsRoute = computed(() =>
    hubspotFeed.value
      ? {
          name: 'data-sources-settings-new',
          params: {
            id: hubspotFeed.value.id,
            tab: DataSourceSettingsTabType.GENERAL,
          },
        }
      : { name: 'data-sources' },
  );

  const connectedAt = computed(() => hubspotFeed.value?.connected_at);

  const connectedBy = computed(
    () =>
      hubspotFeed.value?.external_user_email ?? hubspotFeed.value?.connected_by,
  );

  return {
    statusType,
    statusText,
    needsFeedAttention,
    feedSettingsRoute,
    connectedAt,
    connectedBy,
  };
}
